/*
 * Copyright (C) 2018 BarD Software s.r.o
 */
import * as Auth from './auth/Auth';
import {tradewinds} from './Routes';


export enum NotificationType {
    ProjectCreate,
    ProjectRename,
    ProjectChange,
    ProjectRevert,
    ProjectRemove,
    ProjectLockStatusChange,
    NewInvite,
    ArchiveReady,
}

class NotificationReceiver {
    private ws: WebSocket;
    private readonly handlers = new Map<NotificationType, Consumer<any>>();
    private backoffSec: number = 1;
    private idToken?: string;
    private heartbeat: number = 0;
    public defaultHandler: Consumer<NotificationDto> = () => {};

    public subscribe(idToken: string) {
        this.idToken = idToken;
        this.doSubscribe();
    }

    public registerHandler(t: NotificationType, handler: Consumer<any>) {
        this.handlers[t] = handler;
    }

    private doSubscribe() {
        this.ws = new WebSocket(tradewinds);
        this.ws.onmessage = (evt) => this.handleMessage(evt);
        this.ws.onopen = () => {
            if (!this.idToken) {
                return;
            }
            try {
                this.ws.send(this.idToken);
                this.backoffSec = 1;
                this.setupHeartbeat();
            } catch (e) {
                console.debug(e);
            }
        };
        this.ws.onclose = (evt) => {
            if (evt.code === 1003) {
                console.log(`Bad data, reason=${evt.reason}`);
                if (this.heartbeat !== 0) {
                    window.clearInterval(this.heartbeat);
                }
            } else if (evt.code !== 1000 && window.navigator.onLine) {
                this.resubscribe();
            }
        };

        this.ws.onerror = (evt) => console.log(`WebSocket error:`, evt);
    }

    private resubscribe() {
        this.backoffSec = Math.min(this.backoffSec * 2, 64);
        window.setTimeout(() => this.doSubscribe(), this.backoffSec * 1000);
    }

    private handleMessage(e: MessageEvent) {
        const obj: NotificationDto = JSON.parse(e.data);
        const type = NotificationType[obj.type];
        const handler = this.handlers[type];
        if (handler) {
            handler(obj);
        } else {
            this.defaultHandler(obj);
        }
    }

    private setupHeartbeat() {
        if (this.heartbeat !== 0) {
            window.clearInterval(this.heartbeat);
        }
        this.heartbeat = window.setInterval(() => {
            this.ws.send('HB');
        }, 60 * 1000);
    }
}

const receiver = new NotificationReceiver();

export function start() {
    Auth.getUser().idToken()
        .then((t) => receiver.subscribe(t))
        .catch((e) => console.debug(e));
}

export function registerDefaultHandler(handler: Consumer<NotificationDto>) {
    receiver.defaultHandler = handler;
}

export function registerHandler(t: NotificationType, handler: Consumer<NotificationDto>) {
    receiver.registerHandler(t, handler);
}

interface NotificationUserDto {
    id: string;
    name: string;
}

export interface NotificationDto {
    timestamp: number;
    author?: NotificationUserDto;
    type: string;
    teamRefid: string;
    projectRefid: string;
    teamName?: string;
    projectName?: string;
    newProjectName?: string;
    locked?: boolean;
    archiveId?: string;
}
